import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { CircleFlag } from '@/components/ui/CircleFlag'
import { cn } from '@/lib/cn'
import { countryName, findCountry } from '@/lib/countries'

/* A stored country, READ.
 *
 * The counterpart of CountryDropdown, and it exists because that control stores
 * an alpha-2 code: a list cell printing the column raw says "KZ" where it used
 * to say "Казахстан", which is a worse answer to the same question. So the code
 * is resolved back into the flag and the localised name here, at every surface
 * that displays one.
 *
 * A value that is NOT one of the 249 is printed as it stands. That is not a
 * fallback for bad data but the normal case for everything saved before the
 * field became a picker — the same rule CountryDropdown applies when it offers
 * such a value as a row of its own.
 */
export function CountryText({
  value,
  empty,
  className,
}: {
  /** ISO 3166-1 alpha-2, legacy free text, or '' for unset. */
  value: string | undefined
  /** What to render for an unset value. Nothing renders when omitted. */
  empty?: ReactNode
  className?: string
}) {
  const { i18n } = useTranslation()

  const stored = (value ?? '').trim()
  if (stored === '') return <>{empty ?? null}</>

  const country = findCountry(stored)
  if (!country) return <>{stored}</>

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      {/* The flag is aria-hidden inside CircleFlag: the name beside it is the
          accessible reading, and a country announced twice is noise. */}
      <CircleFlag code={country.code} size={16} />
      {countryName(country, i18n.language)}
    </span>
  )
}
